import { StoresList } from '@/models/enums/app/StoresList'
import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import type { Competence } from '@/models/interfaces/Competence'
import { CompetenceService } from '@/services/competence/CompetenceService'
import { useSousDomaineStore } from '@/stores/sousDomaineStore'

export const useCompetenceStore = defineStore(StoresList.COMPETENCE, () => {
  const competenceList: Ref<Competence[]> = ref([])
  const sousDomaineStore = useSousDomaineStore()

  function extractSousDomaineId(competence: Competence): number | null {
    const raw = competence as unknown as Record<string, unknown>
    const sousDomaineRelation = raw.sousDomaine as Record<string, unknown> | undefined
    const nestedFkSousDomaine = raw.fkSousDomaine as Record<string, unknown> | undefined
    const value =
      raw.fkSousDomaineId ??
      raw.fkSousdomaineId ??
      raw.fkSousDomaineID ??
      raw.fk_sous_domaine_id ??
      raw.fk_sousdomaine_id ??
      raw.fkSousDomainId ??
      raw.sousDomaineId ??
      raw.sous_domaine_id ??
      raw.sousDomaine ??
      raw.sous_domaine ??
      sousDomaineRelation?.sousDomaineId ??
      sousDomaineRelation?.sous_domaine_id ??
      sousDomaineRelation?.id ??
      nestedFkSousDomaine?.sousDomaineId ??
      nestedFkSousDomaine?.sous_domaine_id ??
      nestedFkSousDomaine?.id ??
      raw.fkSousDomaine ??
      raw.fkSsdId
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  async function getAll(): Promise<void> {
    if (competenceList.value.length === 0) {
      competenceList.value = await CompetenceService.getAll()
    }
  }

  /**
   * The Competence API no longer exposes fkDomaineId: a competence relates to its domaine only
   * transitively, through fkSousdomaineId -> Sousdomaine.fkDomaineId. Requires sousDomaineStore
   * to have been populated (sousDomaineStore.getAll()) beforehand.
   */
  function getByDomaineId(domaineId: number): Competence[] {
    const sousDomaineIds = new Set(
      sousDomaineStore.getByDomaineId(domaineId).map((s) => s.sousDomaineId),
    )
    return competenceList.value.filter((competence) => {
      const sousDomaineId = extractSousDomaineId(competence)
      return sousDomaineId !== null && sousDomaineIds.has(sousDomaineId)
    })
  }

  function getBySousDomaineId(sousDomaineId: number): Competence[] {
    return competenceList.value.filter((competence) => extractSousDomaineId(competence) === sousDomaineId)
  }

  return {
    competenceList,
    getAll,
    getByDomaineId,
    getBySousDomaineId,
  }
})
